import React, { useRef, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheckIcon,
  ScaleIcon,
  LockClosedIcon,
  ArrowRightIcon,
  CpuChipIcon,
  PresentationChartLineIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  // ChartPieIcon,
  UserGroupIcon,
  ViewfinderCircleIcon,
  FaceSmileIcon,
  AcademicCapIcon,
  DocumentMagnifyingGlassIcon,
  ArrowTrendingUpIcon,
} from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import Button from "../../components/common/Button";
import BlogImage from "../../components/common/BlogImage";
import TradingViewWidget from "../../components/common/TradingViewWidget";
import { subscriptionsApi, userApi, blogsApi } from "../../services/api";
import { Blog, HeroSettings } from "../../types";
import analystPhoto from "../../assets/rahul-das.png";
import { resolveMediaUrl } from "../../utils/media";
import { createDefaultHeroSettings } from "../../utils/heroSettings";

const SEBI_REG = "INH000023719";

/* ── shared motion preset ─────────────────────────────────────────────── */
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: "easeOut" },
} as const;

import { SharedPlanCard, Plan, BillingPeriod, BillingToggle } from '../../components/subscription/SharedPlanCard';



// The admin marks one plan per billing period as "Most Popular". Since we pass
// only the filtered plans for the current period, this resolves to at most one.
const resolvePopularId = (plans: Plan[]): number | null => {
  const flagged = plans.find(p => p.is_popular);
  return flagged ? flagged.id : null;
};





const homeBlogDate = (value?: string) => {
  if (!value) return "Not published";
  return new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

/* ── billing periods ─────────────────────────────────────────────────── */



const BILLING_ORDER: BillingPeriod[] = ["monthly", "quarterly", "yearly"];



const HomeMobileCarousel: React.FC<{ plans: Plan[] }> = ({ plans }) => {
  const [current, setCurrent] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const popularId = resolvePopularId(plans);

  const scrollTo = (index: number) => {
    const next = Math.max(0, Math.min(index, plans.length - 1));
    setCurrent(next);
    const container = scrollRef.current;
    if (!container) return;
    const card = container.children[next] as HTMLElement;
    if (card) card.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const cardWidth = (container.children[0] as HTMLElement)?.offsetWidth || 1;
    const idx = Math.round(container.scrollLeft / (cardWidth + 24));
    setCurrent(Math.max(0, Math.min(idx, plans.length - 1)));
  };

  return (
    <div className="md:hidden">
      <div className="flex items-center justify-between px-4 mb-3">
        <button type="button" onClick={() => scrollTo(current - 1)} disabled={current === 0}
          className="w-9 h-9 rounded-full bg-dark-700 border border-dark-600 flex items-center justify-center text-gray-300 hover:bg-dark-600 disabled:opacity-30 transition-all">
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <span className="text-xs text-gray-500">{current + 1} / {plans.length}</span>
        <button type="button" onClick={() => scrollTo(current + 1)} disabled={current === plans.length - 1}
          className="w-9 h-9 rounded-full bg-dark-700 border border-dark-600 flex items-center justify-center text-gray-300 hover:bg-dark-600 disabled:opacity-30 transition-all">
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>
      <div ref={scrollRef} onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory gap-6 px-6 pb-4"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
        {plans.map(plan => (
          <div key={plan.id} className="flex-shrink-0 w-[calc(100vw-3rem)] snap-center">
            <SharedPlanCard plan={plan} popular={plan.id === popularId} onGetStarted={() => window.location.href = '/register'} />
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-2 mt-3">
        {plans.map((_, i) => (
          <button key={i} type="button" onClick={() => scrollTo(i)}
            className={`transition-all duration-200 rounded-full ${i === current ? "w-5 h-2 bg-primary-400" : "w-2 h-2 bg-dark-600 hover:bg-dark-500"}`} />
        ))}
      </div>
    </div>
  );
};

/* ── small building blocks ────────────────────────────────────────────── */
const SectionHeading: React.FC<{
  eyebrow?: string;
  title: React.ReactNode;
  subtitle?: string;
  center?: boolean;
}> = ({ eyebrow, title, subtitle, center = true }) => (
  <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""} mb-12`}>
    {eyebrow && (
      <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-primary-400 mb-3">
        {eyebrow}
      </span>
    )}
    <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">{title}</h2>
    {subtitle && <p className="mt-4 text-lg text-dark-300 leading-relaxed">{subtitle}</p>}
  </div>
);

const HomePage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [latestBlogs, setLatestBlogs] = useState<Blog[]>([]);
  const [billing, setBilling] = useState<BillingPeriod>("monthly");
  const [heroSettings, setHeroSettings] = useState<HeroSettings>(createDefaultHeroSettings());

  useEffect(() => {
    subscriptionsApi
      .getPlans(true)
      .then(setPlans)
      .catch(() => {})
      .finally(() => setPlansLoading(false));

    blogsApi.getLatestBlogs(3).then(setLatestBlogs).catch(() => setLatestBlogs([]));
  }, []);

  // Only offer a billing period the admin has actually created plans for
  const availablePeriods = useMemo(
    () => BILLING_ORDER.filter(period => plans.some(p => p.billing_period === period)),
    [plans],
  );

  useEffect(() => {
    if (availablePeriods.length > 0 && !availablePeriods.includes(billing)) {
      setBilling(availablePeriods[0]);
    }
  }, [availablePeriods, billing]);

  const visiblePlans = useMemo(
    () => plans.filter(p => p.billing_period === billing),
    [plans, billing],
  );

  useEffect(() => {
    let mounted = true;

    const loadHeroSettings = async () => {
      try {
        const config = await userApi.getHeroSettings();
        if (mounted) {
          setHeroSettings(config.hero_settings);
        }
      } catch {
        // Keep default fallback content when config fetch fails.
      }
    };

    const handleHeroSettingsUpdated = () => {
      void loadHeroSettings();
    };

    void loadHeroSettings();
    window.addEventListener("hero-settings-updated", handleHeroSettingsUpdated);
    window.addEventListener("hero-image-updated", handleHeroSettingsUpdated);

    return () => {
      mounted = false;
      window.removeEventListener("hero-settings-updated", handleHeroSettingsUpdated);
      window.removeEventListener("hero-image-updated", handleHeroSettingsUpdated);
    };
  }, []);

  const resolvedProfileImage = useMemo(() => {
    return resolveMediaUrl(heroSettings.profile_image_url) || analystPhoto;
  }, [heroSettings.profile_image_url]);

  const featureCardMeta = [
    { icon: DocumentMagnifyingGlassIcon, accent: "text-success-400 bg-success-500/10", align: "ml-auto" },
    { icon: ArrowTrendingUpIcon, accent: "text-purple-400 bg-purple-500/10", align: "mr-auto" },
    { icon: ViewfinderCircleIcon, accent: "text-success-400 bg-success-500/10", align: "ml-auto" },
    { icon: ShieldCheckIcon, accent: "text-purple-400 bg-purple-500/10", align: "ml-auto" },
  ] as const;

  const trustItemMeta = [
    { icon: UserGroupIcon, accent: "text-success-400" },
    { icon: ShieldCheckIcon, accent: "text-success-400" },
    { icon: AcademicCapIcon, accent: "text-purple-400" },
    { icon: LockClosedIcon, accent: "text-purple-400" },
    { icon: FaceSmileIcon, accent: "text-success-400" },
  ] as const;

  return (
    <div className="min-h-screen bg-dark-950 text-white">
      {/* ════════════════════ HERO ════════════════════ */}
      <section className="relative overflow-hidden">
        {/* subtle ambient glow */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-success-600/15 blur-[120px]" />
          <div className="absolute top-10 right-1/4 h-96 w-96 rounded-full bg-purple-600/12 blur-[120px]" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
              backgroundSize: "56px 56px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-8 pb-14 md:pt-10 md:pb-20">
          <div className="grid items-center gap-8 lg:grid-cols-12">
            {/* ── left — floating avatar cards + dashed connector ── */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
              className="relative order-2 lg:order-1 lg:col-span-3"
            >
              {/* dashed connector */}
              <svg
                className="pointer-events-none absolute inset-0 -z-0 h-full w-full"
                viewBox="0 0 200 320"
                fill="none"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M150 40 C60 60 40 120 90 150 C140 180 40 210 120 280"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeDasharray="4 6"
                  className="text-dark-600"
                />
              </svg>

              <div className="relative flex flex-col gap-9">
                {heroSettings.feature_cards.map((card, index) => {
                  const meta = featureCardMeta[index]
                  return (
                  <div
                    key={`${card.title}-${index}`}
                    className={`${meta.align} flex w-[82%] items-center gap-2.5 rounded-xl border border-dark-800 bg-dark-900/80 px-3 py-2 shadow-lg shadow-black/30`}
                  >
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.accent}`}>
                      <meta.icon className="h-4 w-4" aria-hidden="true" focusable="false" />
                    </div>
                    <div className="min-w-0 leading-tight">
                      <p className="text-base font-bold text-white">{card.title}</p>
                      <p className="text-[11px] text-dark-300">{card.subtitle}</p>
                    </div>
                  </div>
                  )
                })}
              </div>
            </motion.div>

            {/* ── center — hero content ── */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="order-1 flex flex-col items-center text-center lg:order-2 lg:col-span-6"
            >
              {/* headline */}
              <h1 className="text-4xl font-bold leading-[1.05] tracking-tight md:text-4xl lg:text-4xl">
                {heroSettings.main_heading}
                <br />
                <span className="bg-gradient-to-r from-success-400 to-success-500 bg-clip-text text-transparent">
                  {heroSettings.highlight_text_1}
                </span>
                <br />
                <span className="bg-gradient-to-r from-purple-400 to-purple-500 bg-clip-text text-transparent">
                  {heroSettings.highlight_text_2}
                </span>
              </h1>

              {/* subtitle */}
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-dark-300">
                {heroSettings.hero_description}
              </p>

              {/* CTAs */}
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Link to="/register">
                  <Button size="lg" className="group w-full sm:w-auto px-8">
                    Get Started
                    <ArrowRightIcon className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden="true" focusable="false" />
                  </Button>
                </Link>
                <a href="#pricing">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto px-8">
                    View Plans
                  </Button>
                </a>
              </div>

              {/* feature chips */}
              {/* <div className="mt-16 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
                {[
                  { icon: DocumentMagnifyingGlassIcon, title: "Research Driven", sub: "Insights", accent: "text-success-400 bg-success-500/10" },
                  { icon: ShieldCheckIcon, title: "Systematic Algo", sub: "Strategies", accent: "text-purple-400 bg-purple-500/10" },
                  { icon: ViewfinderCircleIcon, title: "Risk-First", sub: " Framework", accent: "text-success-400 bg-success-500/10" },
                  { icon: ViewfinderCircleIcon, title: "Risk-First", sub: " Framework", accent: "text-success-400 bg-success-500/10" },
                ].map((c) => (
                  <div key={c.title} className="flex items-center gap-2.5 text-left">
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${c.accent}`}>
                      <c.icon className="h-5 w-5" aria-hidden="true" focusable="false" />
                    </div>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold text-white">{c.title}</p>
                      <p className="text-xs text-dark-300">{c.sub}</p>
                    </div>
                  </div>
                ))}
              </div> */}
            </motion.div>

            {/* ── right — analyst card (white) ── */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
              className="order-3 lg:col-span-3"
            >
              <div className="relative p-2">
                {/* big circular photo — full card width */}
                <div className="relative mx-auto aspect-square w-full max-w-[260px]">
                  <img
                    src={resolvedProfileImage}
                    alt="Rahul Das, Lead Research Analyst"
                    className="h-full w-full rounded-full object-cover ring-4 ring-success-500/20"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = analystPhoto;
                    }}
                  />
                  <span className="absolute bottom-5 right-5 h-6 w-6 rounded-full border-4 border-dark-950 bg-success-500" />
                </div>

                {/* identity */}
                <div className="mt-3 text-center">
                  <p className="text-base font-bold text-white">Rahul Das</p>
                  <p className="text-[11px] font-semibold text-success-400">Lead Research Analyst</p>
                  <p className="text-[11px] font-semibold text-purple-400">Founder | AnalyzerView Market Research</p>
                  <p className="text-[10px] text-dark-400">SEBI Reg. {SEBI_REG}</p>
                </div>

                {/* stats — all in one line */}
                <div className="mt-4 flex items-start justify-between gap-2 border-t border-dark-800 pt-4">
                  {heroSettings.analyst_statistics.map((row, index) => (
                    <div key={`${row.label}-${index}`} className="min-w-0 text-center">
                      <p className={`text-sm font-bold leading-none ${index < 2 ? "text-purple-400" : "text-success-400"}`}>{row.value}</p>
                      <p className="mt-1 truncate text-[10px] text-dark-400">{row.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="relative border-t border-dark-800 bg-dark-900/40"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
              {heroSettings.trust_items.map((item, index) => {
                const meta = trustItemMeta[index]
                return (
                <div key={`${item.title}-${index}`} className="flex items-center justify-center gap-3 text-left">
                  <meta.icon className={`h-8 w-8 shrink-0 ${meta.accent}`} aria-hidden="true" focusable="false" />
                  <div className="leading-tight">
                    <p className="text-sm font-bold text-white">{item.title}</p>
                    <p className="text-xs text-dark-300">{item.subtitle}</p>
                  </div>
                </div>
                )
              })}
            </div>
          </div>
        </motion.div>
      </section>

      {/* ════════════════════ LIVE MARKET ════════════════════ */}
      <section className="w-full">
  <motion.div {...fadeUp}>
    <div className="border-y border-dark-800 bg-dark-950">
      <TradingViewWidget />
    </div>
  </motion.div>
</section>

      {/* ════════════════════ PRICING ════════════════════ */}
      <section id="pricing" className="scroll-mt-20 border-t border-dark-800 px-4 sm:px-6 lg:px-8 py-14">
        <div className="mx-auto max-w-7xl">
          <motion.div {...fadeUp}>
            <SectionHeading
              eyebrow="Pricing"
              title="Choose your investment plan"
              subtitle="Pick the plan that fits your journey. Upgrade or downgrade at any time."
            />
          </motion.div>

          {plansLoading && (
            <div className="flex justify-center py-16">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
            </div>
          )}

          {!plansLoading && plans.length === 0 && (
            <div className="py-16 text-center text-gray-400">
              <p>No plans available yet. Check back soon.</p>
            </div>
          )}

          {/* billing toggle — hidden when only one period exists */}
          {!plansLoading && availablePeriods.length > 1 && (
            <div className="flex justify-center -mt-4 mb-10">
              <BillingToggle value={billing} periods={availablePeriods} onChange={setBilling} />
            </div>
          )}

          {/* desktop */}
          {!plansLoading && visiblePlans.length > 0 && (
            <div className={`hidden md:grid gap-6 items-stretch ${visiblePlans.length <= 3 ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
              {visiblePlans.map((plan, i) => (
                <motion.div key={plan.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} className="h-full">
                  <SharedPlanCard plan={plan} popular={plan.id === resolvePopularId(visiblePlans)} onGetStarted={() => window.location.href = '/register'} />
                </motion.div>
              ))}
            </div>
          )}

          {/* mobile carousel */}
          {!plansLoading && visiblePlans.length > 0 && <HomeMobileCarousel plans={visiblePlans} />}

          <div className="mt-12 text-center">
            <Link
              to="/pricing"
              className="inline-flex items-center text-primary-400 transition-colors hover:text-primary-300"
            >
              View detailed comparison
              <ArrowRightIcon className="ml-2 h-5 w-5" aria-hidden="true" focusable="false" />
            </Link>
          </div>
        </div>
      </section>


      {/* ════════════════════ WHY CHOOSE US ════════════════════ */}
      <section className="px-2 sm:px-2 lg:px-4 py-10 border-t border-dark-800">
        <div className="mx-auto max-w-7xl">
          <motion.div {...fadeUp}>
            <SectionHeading
              eyebrow="Why AnalyzerView"
              title="Built on discipline, not guesswork"
              subtitle="A regulated, process-driven approach to equity research designed to protect capital while pursuing consistent returns."
            />
          </motion.div>

          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                icon: ShieldCheckIcon,
                title: "SEBI Registered",
                body: "Operating under SEBI regulations for full compliance, transparency, and investor protection.",
                accent: "text-primary-400 bg-primary-500/10 border-primary-500/20",
              },
              {
                icon: CpuChipIcon,
                title: "Quantitative Research",
                body: "Data-driven quantitative models combined with expert technical analysis you can actually trust.",
                accent: "text-success-400 bg-success-500/10 border-success-500/20",
              },
              {
                icon: ScaleIcon,
                title: "Risk-Reward Focused",
                body: "Every recommendation comes with calculated entry, exit, and stop levels — structure over speculation.",
                accent: "text-primary-300 bg-primary-500/10 border-primary-500/20",
              },
            ].map((f, i) => (
              <motion.div
                key={f.title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className="group rounded-2xl border border-dark-800 bg-dark-900/60 p-7 transition-all duration-300 hover:border-primary-500/40 hover:bg-dark-900"
              >
                <div
                  className={`mb-5 flex h-14 w-14 items-center justify-center rounded-xl border ${f.accent}`}
                >
                  <f.icon className="h-7 w-7" aria-hidden="true" focusable="false" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-white">{f.title}</h3>
                <p className="leading-relaxed text-dark-300">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* ════════════════════ TOP ANALYST CALLS (teaser) ════════════════════ */}
      <section className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="mx-auto max-w-7xl">
          <motion.div {...fadeUp}>
            <SectionHeading
              center={false}
              eyebrow="Members only"
              title="Top analyst calls"
              subtitle="A live snapshot of our latest recommendations. Subscribe to reveal stocks, entry prices, targets, and reports."
            />
          </motion.div>

          <motion.div {...fadeUp} className="relative overflow-hidden rounded-2xl border border-dark-800 bg-dark-900/60">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
              <thead className="border-b border-dark-800 bg-dark-900">
                <tr>
                  {["Stock", "Call", "Entry", ""].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-400 last:text-right"
                    >
                      {h === "" ? <span className="sr-only">View report</span> : h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-800">
                {[
                  { name: "Infosys", call: "Buy", price: "₹1,380", tone: "success" },
                  { name: "HDFC Bank", call: "Sell", price: "₹1,580", tone: "danger" },
                  { name: "Reliance", call: "Hold", price: "₹2,300", tone: "neutral" },
                  { name: "TCS", call: "Buy", price: "₹3,500", tone: "success" },
                  { name: "ICICI Bank", call: "Buy", price: "₹1,090", tone: "success" },
                ].map((row, i) => {
                  const tone =
                    row.tone === "success"
                      ? "bg-success-500/15 text-success-400 border-success-700/40"
                      : row.tone === "danger"
                      ? "bg-danger-500/15 text-danger-400 border-danger-700/40"
                      : "bg-dark-700/60 text-dark-200 border-dark-600";
                  return (
                    <tr key={i} className="transition-colors hover:bg-dark-800/40">
                      <td className="px-5 py-4 font-medium text-white">
                        <span className="select-none blur-sm">{row.name}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${tone}`}
                        >
                          {row.call}
                        </span>
                      </td>
                      <td className="px-5 py-4 font-mono text-dark-200">
                        <span className="select-none blur-sm">{row.price}</span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <Link to="/register">
                          <Button size="sm" variant="outline" className="!px-4">
                            View report
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {/* unlock overlay */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center bg-gradient-to-t from-dark-950 via-dark-950/70 to-transparent pb-6 pt-20">
              <Link to="/register" className="pointer-events-auto">
                <Button size="lg" className="shadow-xl shadow-primary-900/40">
                  <LockClosedIcon className="mr-2 h-5 w-5" aria-hidden="true" focusable="false" />
                  Subscribe to unlock all calls
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════ MODEL PORTFOLIOS (teaser) ════════════════════ */}
      <section className="px-4 sm:px-6 lg:px-8 py-16">
        <div className="mx-auto max-w-7xl">
          <motion.div {...fadeUp}>
            <SectionHeading
              eyebrow="Members only"
              title="Model portfolios"
              subtitle="Professionally curated portfolios for different goals and risk profiles. Allocations and returns unlock with a subscription."
            />
          </motion.div>

          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                name: "Dividend",
                risk: "Low",
                ring: "text-success-500",
                badge: "bg-success-500/15 text-success-400 border-success-700/40",
                pct: 2 / 5,
                desc: "Income-focused — dividend stocks and bonds for steady cash flow and capital preservation.",
                rows: [["Dividend Stocks", "60%"], ["REITs", "15%"], ["Bonds/Debt", "20%"], ["Cash", "5%"]],
                ret: "8–12% p.a.",
                horizon: "2–3 years",
              },
              {
                name: "Balanced",
                risk: "Medium",
                ring: "text-amber-500",
                badge: "bg-amber-500/15 text-amber-400 border-amber-700/40",
                pct: 3 / 5,
                desc: "Well-diversified — a mix of equities, bonds, and defensive assets balancing growth and stability.",
                rows: [["Large Cap", "50%"], ["Mid Cap", "20%"], ["Bonds/Debt", "25%"], ["Cash", "5%"]],
                ret: "12–15% p.a.",
                horizon: "3–5 years",
                popular: true,
              },
              {
                name: "Growth",
                risk: "High",
                ring: "text-danger-500",
                badge: "bg-danger-500/15 text-danger-400 border-danger-700/40",
                pct: 4 / 5,
                desc: "Aggressive — high-conviction equity in emerging sectors and growth stocks for maximum upside.",
                rows: [["Large Cap", "40%"], ["Mid Cap", "35%"], ["Small Cap", "20%"], ["Cash/Bonds", "5%"]],
                ret: "15–20% p.a.",
                horizon: "5+ years",
              },
            ].map((p, i) => (
              <motion.div
                key={p.name}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className={`relative rounded-2xl border bg-dark-900/60 p-6 transition-all duration-300 hover:-translate-y-1 ${
                  p.popular ? "border-primary-500/60 shadow-lg shadow-primary-900/30" : "border-dark-800 hover:border-primary-500/40"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary-600 px-4 py-1 text-xs font-semibold text-white">
                    Most Popular
                  </span>
                )}

                <div className="mb-4 flex items-start justify-between">
                  <h3 className="text-2xl font-bold text-white">{p.name}</h3>
                  <div className="relative h-12 w-12">
                    <svg className="h-12 w-12 -rotate-90" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <circle cx="12" cy="12" r="8" fill="none" strokeWidth="2.5" className="stroke-dark-700" />
                      <circle
                        cx="12"
                        cy="12"
                        r="8"
                        fill="none"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeDasharray={`${p.pct * 50.26} 50.26`}
                        className={`stroke-current ${p.ring}`}
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-dark-300" aria-hidden="true">
                      {p.risk[0]}
                    </span>
                  </div>
                </div>

                <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${p.badge}`}>
                  {p.risk} Risk
                </span>

                <p className="my-5 text-sm leading-relaxed text-dark-300">{p.desc}</p>

                <div className="space-y-2.5 border-t border-dark-800 pt-4">
                  {p.rows.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-sm">
                      <span className="text-dark-300">{k}</span>
                      <span className="select-none font-medium text-primary-300 blur-[3px]">{v}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 space-y-2 border-t border-dark-800 pt-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Expected return</span>
                    <span className="select-none font-medium text-success-400 blur-[3px]">{p.ret}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Time horizon</span>
                    <span className="select-none font-medium text-white blur-[3px]">{p.horizon}</span>
                  </div>
                </div>

                <Link to="/register" className="mt-6 block">
                  <Button variant={p.popular ? "primary" : "outline"} className="w-full">
                    <LockClosedIcon className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" /> Unlock details
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            {...fadeUp}
            className="mt-8 flex items-start gap-3 rounded-xl border border-dark-800 bg-dark-900/40 p-5"
          >
            <span className="mt-0.5 text-amber-400" aria-hidden="true">⚠</span>
            <p className="text-sm text-gray-400">
              <span className="font-medium text-amber-400">Disclaimer:</span> Past performance does
              not guarantee future results. These model portfolios are illustrative and not
              investment advice. Please consult our SEBI-registered research analyst before
              investing.
            </p>
          </motion.div>
        </div>
      </section>

            {/* ════════════════════ BLOGS ════════════════════ */}
      <section className="px-2 sm:px-4 lg:px-4 py-8">
        <div className="mx-auto max-w-7xl">
          <motion.div {...fadeUp}>
            <SectionHeading
              eyebrow="Insights"
              title="Latest from our blog"
              subtitle="Fresh market commentary, educational explainers, and tactical notes from our research desk."
            />
          </motion.div>

          <div className="grid gap-6 md:grid-cols-3">
            {latestBlogs.map((blog, i) => (
              <motion.article
                key={blog.id}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="rounded-2xl border border-dark-800 bg-dark-900/60 overflow-hidden"
              >
                <div className="aspect-[16/10] bg-dark-800">
                  <BlogImage
                    src={blog.featured_image}
                    alt={blog.title}
                    className="w-full h-full object-cover"
                    fallbackClassName="w-full h-full flex items-center justify-center text-xs text-gray-500"
                    fallbackText="No image"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="px-2 py-1 rounded-full bg-primary-500/15 text-primary-300 border border-primary-500/30">
                      {blog.category || "General"}
                    </span>
                    <span className="text-gray-500">{blog.read_time || "5 min read"}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white line-clamp-2">{blog.title}</h3>
                  <p className="mt-2 text-sm text-gray-300 line-clamp-3">{blog.short_description || blog.summary || "Read the full article for details."}</p>
                  <p className="mt-3 text-xs text-gray-500">
                    {homeBlogDate(blog.published_at || blog.created_at)} · {blog.author || "AnalyzerView Desk"}
                  </p>
                  <Link to={`/blogs/${blog.slug}`} className="mt-4 inline-flex items-center text-sm text-primary-400 hover:text-primary-300">
                    Read More
                    <ArrowRightIcon className="ml-1 h-4 w-4" aria-hidden="true" focusable="false" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link to="/blogs" className="inline-flex items-center text-primary-400 hover:text-primary-300">
              View all blogs
              <ArrowRightIcon className="ml-2 h-5 w-5" aria-hidden="true" focusable="false" />
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════════════ TESTIMONIALS ════════════════════ */}
      <section className="border-t border-dark-800 px-4 sm:px-6 lg:px-8 py-24">
        <div className="mx-auto max-w-7xl">
          <motion.div {...fadeUp}>
            <SectionHeading
              eyebrow="Testimonials"
              title="Trusted by serious investors"
              subtitle="Why professionals and individuals rely on AnalyzerView for their investment decisions."
            />
          </motion.div>

          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                quote:
                  "The research is clear and actionable. Every call comes with a defined entry, target, and stop — exactly the discipline I was missing.",
                author: "Priya Sharma",
                role: "Software Engineer",
              },
              {
                quote:
                  "I went from guessing to following a structured process. The risk-reward framing alone changed how I invest.",
                author: "Rajesh Kumar",
                role: "Marketing Manager",
              },
              {
                quote:
                  "Being SEBI registered gave me the confidence to trust their recommendations. Professional and transparent throughout.",
                author: "Anita Desai",
                role: "Financial Analyst",
              },
            ].map((t, i) => (
              <motion.div
                key={t.author}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className="flex flex-col rounded-2xl border border-dark-800 bg-dark-900/60 p-7"
              >
                <div className="mb-4 flex gap-0.5">
                  <span className="sr-only">Rated 5 out of 5 stars</span>
                  {Array.from({ length: 5 }).map((_, s) => (
                    <StarIcon key={s} className="h-4 w-4 text-amber-400" aria-hidden="true" focusable="false" />
                  ))}
                </div>
                <p className="flex-1 leading-relaxed text-dark-200">“{t.quote}”</p>
                <div className="mt-6 flex items-center gap-3 border-t border-dark-800 pt-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-success-500 text-sm font-semibold text-white">
                    {t.author.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{t.author}</p>
                    <p className="text-xs text-gray-400">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ FINAL CTA ════════════════════ */}
      <section className="px-4 sm:px-6 lg:px-8 pb-24">
        <motion.div {...fadeUp} className="mx-auto max-w-5xl">
          <div className="relative overflow-hidden rounded-3xl border border-primary-500/30 bg-gradient-to-br from-primary-900/40 via-dark-900 to-dark-900 p-10 md:p-14">
            <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary-600/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-success-500/10 blur-3xl" />
            <div className="relative flex flex-col items-center gap-8 text-center md:flex-row md:justify-between md:text-left">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-primary-300">
                  <PresentationChartLineIcon className="h-5 w-5" aria-hidden="true" focusable="false" />
                  Start today
                </div>
                <h2 className="max-w-xl text-3xl font-bold text-white md:text-4xl">
                  Make informed investment decisions, backed by research.
                </h2>
              </div>
              <Link to="/register" className="shrink-0">
                <Button size="lg" className="group px-8">
                  Sign Up
                  <ArrowRightIcon className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden="true" focusable="false" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default HomePage;
